import React, { useState } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag, CreditCard, Lock, Truck, Shield } from 'lucide-react';
import './Cart.css';

function Cart({ cart, onClose, onUpdateQuantity, onRemove, onCheckout, total }) {
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate subtotal and NYC tax
  const subtotal = total;
  const nycTaxRate = 0.08875; // 8.875% NYC tax
  const tax = subtotal * nycTaxRate;
  const finalTotal = subtotal + tax;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    
    setIsProcessing(true);
    try {
      await onCheckout();
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="cart-overlay" onClick={onClose}>
      <div className="cart-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <h2>Shopping Cart</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="cart-content">
          {cart.length === 0 ? (
            <div className="empty-cart">
              <ShoppingBag size={64} color="#d1d5db" />
              <h3>Your cart is empty</h3>
              <p>Add some items to get started!</p>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} />
                      ) : (
                        <div className="placeholder-thumb">
                          <ShoppingBag size={24} />
                        </div>
                      )}
                    </div>

                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p className="item-category">{item.category}</p>
                      <div className="item-price-row">
                        <p className="item-price">${item.price.toFixed(2)}</p>
                        <p className="item-total">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="item-actions">
                      <div className="quantity-controls">
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="qty-btn"
                          disabled={item.quantity <= 1}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="quantity">{item.quantity}</span>
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="qty-btn"
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      <button 
                        className="remove-btn"
                        onClick={() => onRemove(item.id)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="order-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Tax (NYC 8.875%)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>FREE</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-total">
                  <span>Total</span>
                  <span>${finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="badge">
                  <Lock size={16} />
                  <span>Secure Payment</span>
                </div>
                <div className="badge">
                  <Truck size={16} />
                  <span>Fast Delivery</span>
                </div>
                <div className="badge">
                  <Shield size={16} />
                  <span>Buyer Protection</span>
                </div>
              </div>

              <div className="cart-footer">
                <button 
                  className="checkout-btn" 
                  onClick={handleCheckout}
                  disabled={isProcessing || cart.length === 0}
                >
                  {isProcessing ? (
                    <>
                      <div className="spinner"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} />
                      Proceed to Checkout
                    </>
                  )}
                </button>
                
                <div className="checkout-note">
                  <p>🔒 Secure checkout powered by Stripe</p>
                  <p>🚚 Free shipping on all orders</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Cart;
